import { Component, ElementRef, Input, OnChanges, OnDestroy, ViewChild } from '@angular/core';
import { ScApiService } from '@sc-ui';
import Chart from 'chart.js/auto';
import { texts } from './project-outreach.texts';
import { LocalizationService } from 'src/services/localization.service';
import { DemoService } from 'src/services/demo.service';

@Component({
  standalone: false,
  selector: 'app-project-outreach',  
  templateUrl: './project-outreach.html',
  styleUrl: './project-outreach.scss',
})
export class ProjectOutreach implements OnChanges, OnDestroy {

  texts = texts;

  @Input()
  project: any;

  outreachChart?: Chart;

  @ViewChild('targetBadge')
  targetBadge: ElementRef<HTMLElement>;

  // keeps the "Ziel" badge a little in from the start of the target line, centered on it,
  // after every draw (resizes included)
  private targetBadgePlugin = {
    id: 'targetBadge',
    afterDraw: (chart: Chart) => {
      var badge = this.targetBadge?.nativeElement;
      var lineStart = chart.getDatasetMeta(0).data[0];
      if (!badge || !lineStart) return;
      badge.style.left = (lineStart.x + 20) + 'px';
      badge.style.top = chart.scales['y'].getPixelForValue(this.outreachTarget) + 'px';
      badge.style.visibility = 'visible';
    }
  };

  // the outreach card only renders in demo mode, so the chart follows its canvas
  @ViewChild('outreachChart')
  set outreachChartCanvas(canvas: ElementRef<HTMLCanvasElement>) {
    this.outreachChart?.destroy();
    this.outreachChart = canvas ? this.createOutreachChart(canvas.nativeElement) : undefined;
  }

  // Hardcoded - no API yet. Contacts cumulated per calendar week.
  outreachWeeks = [
    { Label: 'KW 31', Value: 12 },
    { Label: 'KW 32', Value: 37 },
    { Label: 'KW 33', Value: 45 },
    { Label: 'KW 34', Value: 76 },
    { Label: 'KW 35', Value: 93 },
    { Label: 'KW 36', Value: 101 },
    { Label: 'KW 37', Value: 129 },
    { Label: 'KW 38', Value: 142 }
  ];

  outreachTarget = 150;

  outreachKpis = { contacted: 142, clicks: 38, applications: 12 };

  // Hardcoded - no API yet. What candidates answered when they declined.
  feedbackSummary = 'Die meisten Absagen betreffen die Aufgaben und das Gehalt: Viele Angesprochene sehen sich ' +
    'eher in der Umsetzung als in der Beratung, und für Senior-Profile liegt die Gehaltsspanne unter ihren ' +
    'Erwartungen. Die Präsenz vor Ort ist das dritthäufigste Thema – ein höherer Remote-Anteil wird mehrfach als ' +
    'Bedingung genannt. Wer grundsätzlich interessiert ist, fragt häufig nach Teamgröße und Projektzuschnitt.';

  // share of all rejections, by the chat's reject reason keys, and how the
  // project compares to the benchmark on it (better = less often a reason)
  rejectReasons = [
    { Key: 'tasks', Share: 27, Benchmark: 'neutral' },
    { Key: 'salary', Share: 22, Benchmark: 'worse' },
    { Key: 'location', Share: 17, Benchmark: 'worse' },
    { Key: 'career_level', Share: 13, Benchmark: 'better' },
    { Key: 'timing', Share: 11, Benchmark: 'neutral' },
    { Key: 'employer', Share: 4, Benchmark: 'better' },
    { Key: 'other', Share: 6, Benchmark: 'neutral' }
  ];

  feedbackMessages = [
    {
      Reason: 'tasks',
      Text: 'Danke für die Nachricht! Die Rolle klingt spannend, aber ich bin aktuell stärker in der Umsetzung ' +
        'unterwegs und möchte das auch bleiben.'
    },
    {
      Reason: 'salary',
      Text: 'Klingt interessant, die Spanne liegt allerdings unter meinem aktuellen Gehalt. Ab 85k würde ich mich ' +
        'gerne austauschen.'
    },
    {
      Reason: 'location',
      Text: 'Drei Tage pro Woche im Büro sind für mich leider nicht machbar. Bei überwiegend remote gerne.'
    },
    {
      Reason: 'timing',
      Text: 'Ich habe gerade erst gewechselt – melden Sie sich gern in einem Jahr noch einmal.'
    },
    {
      Reason: 'career_level',
      Text: 'Ich suche als Nächstes eine Position mit Personalverantwortung, das passt hier leider nicht.'
    }
  ];

  editedDescription = '';

  editedHighlights = '';

  isSaving = false;

  constructor(private api: ScApiService, 
    public loc: LocalizationService,
    public demo: DemoService) { }

  ngOnChanges() {
    this.discard();
  }

  createOutreachChart(canvas: HTMLCanvasElement) {

    // the line takes the green of its badge, so both are the design system's success color
    var badge = canvas.parentElement.querySelector('.target-badge');
    var success = badge ? getComputedStyle(badge).backgroundColor : '#1fc267';

    return new Chart(canvas, {
      type: 'bar',
      plugins: [this.targetBadgePlugin],
      data: {
        labels: this.outreachWeeks.map(w => w.Label),
        datasets: [
          {
            type: 'line',
            label: texts.outreach.target[this.loc.language],
            data: this.outreachWeeks.map(() => this.outreachTarget),
            borderColor: success,
            borderWidth: 2,
            pointRadius: 0,
            fill: false
          },
          {
            type: 'bar',
            label: texts.outreach.contacted[this.loc.language],
            data: this.outreachWeeks.map(w => w.Value),
            backgroundColor: 'rgba(0,98,215,0.8)',
            borderColor: 'rgba(0,98,215,0.8)',
            borderRadius: 8,
            // half the default bar width (0.9)
            barPercentage: 0.45
          }
        ]
      },
      options: {
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            suggestedMax: this.outreachTarget + 20
          }
        }
      }
    });
  }

  ngOnDestroy() {
    this.outreachChart?.destroy();
  }

  // Profile.Highlights is a single newline separated string, so the textarea
  // edits it as it is stored and the jobsite splits it into its benefit list
  discard() {
    this.editedDescription = this.project?.Profile?.Description ?? '';
    this.editedHighlights = this.project?.Profile?.Highlights ?? '';
  }

  save() {

    if (this.isSaving) return;
    this.isSaving = true;

    this.project.Profile.Description = this.editedDescription;
    this.project.Profile.Highlights = this.editedHighlights;

    this.api.put('projects', this.project).subscribe(() => this.isSaving = false);
  }
}
